import { zippyCoreBlockchain } from '@zippyhealth/zippycore-blockchain';

// Encryption algorithms and key management
export interface EncryptionKey {
  id: string;
  algorithm: 'AES-256-GCM';
  keyHash: string; // Never store actual key, only hash
  salt: string;
  iterations: number;
  createdAt: string;
  rotatedAt?: string;
  status: 'active' | 'rotated' | 'compromised';
}

export interface EncryptedData {
  id: string;
  dataType: 'health_record' | 'lab_result' | 'medication' | 'symptom' | 'food_log' | 'document';
  encryptedData: string;
  iv: string;
  authTag: string;
  keyId: string;
  createdAt: string;
  updatedAt: string;
  metadata: {
    originalSize: number;
    compressionRatio?: number;
    backupStatus: 'local_only' | 'cloud_backed' | 'blockchain_stored';
    zippyCoinCost?: number;
  };
}

export interface DeviceSecurityProfile {
  deviceId: string;
  deviceType: 'mobile' | 'tablet' | 'desktop' | 'laptop';
  securityLevel: 'basic' | 'enhanced' | 'enterprise';
  encryptionCapable: boolean;
  biometricAvailable: boolean;
  secureEnclaveAvailable: boolean;
  lastSecurityCheck: string;
  securityScore: number; // 0-100
}

export interface CloudBackupOptions {
  enabled: boolean;
  autoBackup: boolean;
  backupFrequency: 'daily' | 'weekly' | 'monthly';
  retentionPeriod: '30_days' | '90_days' | '1_year' | 'forever';
  encryptionLevel: 'standard' | 'enhanced' | 'zero_knowledge';
  zippyCoinCost: number;
  storageUsed: number;
  storageLimit: number;
}

export class LocalDataEncryption {
  private static instance: LocalDataEncryption;
  private currentKey: CryptoKey | null = null;
  private deviceProfile: DeviceSecurityProfile | null = null;
  private backupOptions: CloudBackupOptions | null = null;

  private constructor() {
    this.initializeDeviceProfile();
  }

  public static getInstance(): LocalDataEncryption {
    if (!LocalDataEncryption.instance) {
      LocalDataEncryption.instance = new LocalDataEncryption();
    }
    return LocalDataEncryption.instance;
  }

  /**
   * Initialize device security profile and check capabilities
   */
  private async initializeDeviceProfile(): Promise<void> {
    const deviceId = this.getDeviceId();
    const deviceType = this.getDeviceType();
    
    // Check device capabilities
    const encryptionCapable = await this.checkEncryptionCapability();
    const biometricAvailable = await this.checkBiometricAvailability();
    const secureEnclaveAvailable = await this.checkSecureEnclaveAvailability();
    
    this.deviceProfile = {
      deviceId,
      deviceType,
      securityLevel: this.determineSecurityLevel(encryptionCapable, biometricAvailable, secureEnclaveAvailable),
      encryptionCapable,
      biometricAvailable,
      secureEnclaveAvailable,
      lastSecurityCheck: new Date().toISOString(),
      securityScore: this.calculateSecurityScore(encryptionCapable, biometricAvailable, secureEnclaveAvailable)
    };

    // Store device profile
    localStorage.setItem('zippyhealth_device_profile', JSON.stringify(this.deviceProfile));
  }

  /**
   * Generate or retrieve encryption key
   */
  public async getEncryptionKey(): Promise<CryptoKey> {
    if (this.currentKey) {
      return this.currentKey;
    }

    // Check for existing key in secure storage
    const existingKey = await this.retrieveStoredKey();
    if (existingKey) {
      this.currentKey = existingKey;
      return existingKey;
    }

    // Generate new key
    const newKey = await this.generateNewKey();
    this.currentKey = newKey;
    await this.storeKey(newKey);
    
    return newKey;
  }

  /**
   * Encrypt data with device-level security
   */
  public async encryptData(data: any, dataType: EncryptedData['dataType']): Promise<EncryptedData> {
    const key = await this.getEncryptionKey();
    const dataString = JSON.stringify(data);
    const dataBuffer = new TextEncoder().encode(dataString);
    
    // Generate IV
    const iv = crypto.getRandomValues(new Uint8Array(12));
    
    // Encrypt data
    const encryptedBuffer = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      dataBuffer
    );
    
    // Extract auth tag (last 16 bytes)
    const authTag = encryptedBuffer.slice(-16);
    const encryptedData = encryptedBuffer.slice(0, -16);
    
    const encryptedDataObj: EncryptedData = {
      id: crypto.randomUUID(),
      dataType,
      encryptedData: this.arrayBufferToBase64(encryptedData),
      iv: this.arrayBufferToBase64(iv),
      authTag: this.arrayBufferToBase64(authTag),
      keyId: 'device_key_1', // In production, use actual key ID
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      metadata: {
        originalSize: dataString.length,
        backupStatus: 'local_only',
        zippyCoinCost: 0
      }
    };

    return encryptedDataObj;
  }

  /**
   * Decrypt data
   */
  public async decryptData(encryptedData: EncryptedData): Promise<any> {
    const key = await this.getEncryptionKey();
    
    // Convert base64 to ArrayBuffer
    const encryptedBuffer = this.base64ToArrayBuffer(encryptedData.encryptedData);
    const iv = this.base64ToArrayBuffer(encryptedData.iv);
    const authTag = this.base64ToArrayBuffer(encryptedData.authTag);
    
    // Combine encrypted data and auth tag
    const combinedBuffer = new Uint8Array(encryptedBuffer.byteLength + authTag.byteLength);
    combinedBuffer.set(new Uint8Array(encryptedBuffer), 0);
    combinedBuffer.set(new Uint8Array(authTag), encryptedBuffer.byteLength);
    
    // Decrypt
    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      combinedBuffer
    );
    
    const decryptedString = new TextDecoder().decode(decryptedBuffer);
    return JSON.parse(decryptedString);
  }

  /**
   * Store encrypted data locally
   */
  public async storeEncryptedData(data: any, dataType: EncryptedData['dataType']): Promise<string> {
    const encryptedData = await this.encryptData(data, dataType);
    const storageKey = `zippyhealth_encrypted_${dataType}_${encryptedData.id}`;
    
    localStorage.setItem(storageKey, JSON.stringify(encryptedData));
    
    // Update storage statistics
    this.updateStorageStats(encryptedData);
    
    return encryptedData.id;
  }

  /**
   * Retrieve encrypted data by ID
   */
  public async getEncryptedData(dataId: string, dataType: EncryptedData['dataType']): Promise<any> {
    const storageKey = `zippyhealth_encrypted_${dataType}_${dataId}`;
    const storedData = localStorage.getItem(storageKey);
    
    if (!storedData) {
      throw new Error(`Encrypted data not found: ${dataId}`);
    }
    
    const encryptedData: EncryptedData = JSON.parse(storedData);
    return await this.decryptData(encryptedData);
  }

  /**
   * Backup data to cloud with ZippyCoin payment
   */
  public async backupToCloud(dataId: string, dataType: EncryptedData['dataType']): Promise<boolean> {
    try {
      // Get encrypted data
      const storageKey = `zippyhealth_encrypted_${dataType}_${dataId}`;
      const storedData = localStorage.getItem(storageKey);
      
      if (!storedData) {
        throw new Error(`Data not found for backup: ${dataId}`);
      }
      
      const encryptedData: EncryptedData = JSON.parse(storedData);
      
      // Calculate backup cost in ZippyCoins
      const backupCost = this.calculateBackupCost(encryptedData);
      
      // Check ZippyCoin balance
      const wallet = await zippyCoreBlockchain.loadUserWallet('current_user');
      if (wallet.balance < backupCost) {
        throw new Error(`Insufficient ZippyCoins for backup. Required: ${backupCost}, Available: ${wallet.balance}`);
      }
      
      // Process payment
      await zippyCoreBlockchain.processAIUsagePayment(backupCost, 'cloud_backup_service');
      
      // Update backup status
      encryptedData.metadata.backupStatus = 'cloud_backed';
      encryptedData.metadata.zippyCoinCost = backupCost;
      encryptedData.updatedAt = new Date().toISOString();
      
      // Store updated data
      localStorage.setItem(storageKey, JSON.stringify(encryptedData));
      
      // Update backup options
      this.updateBackupOptions(backupCost);
      
      return true;
      
    } catch (error) {
      console.error('Backup failed:', error);
      return false;
    }
  }

  /**
   * Get device security profile
   */
  public getDeviceProfile(): DeviceSecurityProfile | null {
    return this.deviceProfile;
  }

  /**
   * Get cloud backup options
   */
  public getBackupOptions(): CloudBackupOptions | null {
    return this.backupOptions;
  }

  /**
   * Test device encryption capabilities
   */
  public async testDeviceEncryption(): Promise<{
    success: boolean;
    capabilities: {
      encryption: boolean;
      biometric: boolean;
      secureEnclave: boolean;
    };
    recommendations: string[];
  }> {
    const capabilities = {
      encryption: await this.checkEncryptionCapability(),
      biometric: await this.checkBiometricAvailability(),
      secureEnclave: await this.checkSecureEnclaveAvailability()
    };

    const recommendations: string[] = [];
    
    if (!capabilities.encryption) {
      recommendations.push('Your device may not support secure encryption. Consider using a different device for sensitive health data.');
    }
    
    if (!capabilities.biometric) {
      recommendations.push('Enable biometric authentication for enhanced security.');
    }
    
    if (!capabilities.secureEnclave) {
      recommendations.push('Consider using a device with secure enclave for maximum security.');
    }

    return {
      success: capabilities.encryption,
      capabilities,
      recommendations
    };
  }

  // Private helper methods

  private getDeviceId(): string {
    let deviceId = localStorage.getItem('zippyhealth_device_id');
    if (!deviceId) {
      deviceId = crypto.randomUUID();
      localStorage.setItem('zippyhealth_device_id', deviceId);
    }
    return deviceId;
  }

  private getDeviceType(): DeviceSecurityProfile['deviceType'] {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/mobile|android|iphone|ipad|ipod/.test(userAgent)) {
      return /tablet|ipad/.test(userAgent) ? 'tablet' : 'mobile';
    }
    return /macintosh|mac os/.test(userAgent) ? 'laptop' : 'desktop';
  }

  private async checkEncryptionCapability(): Promise<boolean> {
    try {
      // Test Web Crypto API
      const testKey = await crypto.subtle.generateKey(
        { name: 'AES-GCM', length: 256 },
        true,
        ['encrypt', 'decrypt']
      );
      return true;
    } catch {
      return false;
    }
  }

  private async checkBiometricAvailability(): Promise<boolean> {
    // Check for WebAuthn support
    return 'credentials' in navigator && 'create' in window.PublicKeyCredential;
  }

  private async checkSecureEnclaveAvailability(): Promise<boolean> {
    // This would require platform-specific APIs
    // For now, return false and let platform detection handle it
    return false;
  }

  private determineSecurityLevel(
    encryption: boolean,
    biometric: boolean,
    secureEnclave: boolean
  ): DeviceSecurityProfile['securityLevel'] {
    if (secureEnclave) return 'enterprise';
    if (encryption && biometric) return 'enhanced';
    return 'basic';
  }

  private calculateSecurityScore(
    encryption: boolean,
    biometric: boolean,
    secureEnclave: boolean
  ): number {
    let score = 0;
    if (encryption) score += 40;
    if (biometric) score += 30;
    if (secureEnclave) score += 30;
    return score;
  }

  private async generateNewKey(): Promise<CryptoKey> {
    return await crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    );
  }

  private async retrieveStoredKey(): Promise<CryptoKey | null> {
    try {
      const storedKeyData = localStorage.getItem('zippyhealth_encryption_key');
      if (!storedKeyData) return null;
      
      // In production, implement proper key storage
      // For now, return null to generate new key
      return null;
    } catch {
      return null;
    }
  }

  private async storeKey(key: CryptoKey): Promise<void> {
    // In production, implement secure key storage
    // For now, just store a reference
    localStorage.setItem('zippyhealth_encryption_key', 'stored');
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  private updateStorageStats(encryptedData: EncryptedData): void {
    const stats = JSON.parse(localStorage.getItem('zippyhealth_storage_stats') || '{}');
    stats.totalEncryptedItems = (stats.totalEncryptedItems || 0) + 1;
    stats.totalStorageUsed = (stats.totalStorageUsed || 0) + encryptedData.encryptedData.length;
    stats.lastUpdated = new Date().toISOString();
    localStorage.setItem('zippyhealth_storage_stats', JSON.stringify(stats));
  }

  private calculateBackupCost(encryptedData: EncryptedData): number {
    // Base cost: 0.1 ZippyCoins per MB
    const sizeInMB = encryptedData.encryptedData.length / (1024 * 1024);
    const baseCost = sizeInMB * 0.1;
    
    // Add premium for sensitive data types
    const sensitiveTypes = ['lab_result', 'document'];
    const premium = sensitiveTypes.includes(encryptedData.dataType) ? 0.05 : 0;
    
    return Math.max(0.01, baseCost + premium); // Minimum 0.01 ZippyCoins
  }

  private updateBackupOptions(cost: number): void {
    if (!this.backupOptions) {
      this.backupOptions = {
        enabled: true,
        autoBackup: false,
        backupFrequency: 'weekly',
        retentionPeriod: '90_days',
        encryptionLevel: 'enhanced',
        zippyCoinCost: 0,
        storageUsed: 0,
        storageLimit: 1024 * 1024 * 100 // 100MB
      };
    }
    
    this.backupOptions.zippyCoinCost += cost;
    this.backupOptions.storageUsed += 1024; // Approximate size
    
    localStorage.setItem('zippyhealth_backup_options', JSON.stringify(this.backupOptions));
  }
}

// Export singleton instance
export const localDataEncryption = LocalDataEncryption.getInstance(); 